package nl.volleyplanner.sportconfig

import nl.volleyplanner.extensions.MonthDay
import nl.volleyplanner.types.AgeGroup
import nl.volleyplanner.types.Gender

object NevoboAgeGroup {
    val CMV = AgeGroup("CMV", "CMV-Jeugd", 5, 12)
    val C = AgeGroup("C", "C-Jeugd", 12, 14)
    val B = AgeGroup("B", "B-Jeugd", 14, 16)
    val A = AgeGroup("A", "A-Jeugd", 16, 18)
    val Senior = AgeGroup("Senior", "Senioren", 18, 99)

    val YOUTH_GROUPS = setOf(A, B, C)
}

object NevoboConfig : SportConfig(
    cutOffMonthDay = MonthDay.of(9, 1),
    ageGroups = listOf(NevoboAgeGroup.Senior, NevoboAgeGroup.A, NevoboAgeGroup.B, NevoboAgeGroup.C, NevoboAgeGroup.CMV)
        .associateBy { it.identifier }
) {

}

enum class NevoboCompetitionType {
    IndoorVolleyball,
    IndoorMasterCompetitionVolleyball,
    IndoorRecreationalVolleyball,
    SittingVolleyball
}

data class NevoboTeamName(
    /**
     * Name of the club of the team, can include a sponsor name
     */
    val clubName: String,
    /**
     * The type of competion the team is playing in
     */
    val nevoboCompetitionType: NevoboCompetitionType,
    /**
     * The gender of the team
     */
    val gender: Gender,
    /**
     * The age group of the team
     */
    val ageGroup: AgeGroup,
    /**
     * If this is a CMV team, the level they are playing at
     */
    val cmvLevel: Int?,
    /**
     * The team number
     */
    val teamNumber: Int,
    /**
     * The unique piece of the team. e.g. 'JA 1'
     */
    val teamIdentifier: String,
    /**
     * The full team name as provided
     */
    val fullTeamName: String
) {
    companion object {
        private val cache = mutableMapOf<String, NevoboTeamName>()
        private val pattern = Regex("^(?<club>.+?)\\s+(?<gender>[JMDHXNL])(?<ageGroup>[SRABCZJM1-6])\\s?(?<teamNumber>\\d{1,2})?$")

        /**
         * Parses a team name as provided by Nevobo into the type of team for further use
         */
        fun parse(teamName: String): NevoboTeamName {
            val cachedResult = cache[teamName]
            if (cachedResult != null) return cachedResult

            val matchResult = pattern.matchEntire(teamName) ?: throw IllegalArgumentException("Could not parse $teamName")

            val club = matchResult.groups["club"]!!.value
            val genderChar = matchResult.groups["gender"]!!.value[0]
            val ageGroupChar = matchResult.groups["ageGroup"]!!.value[0]
            val teamNumber = matchResult.groups["teamNumber"]?.value?.toInt() ?: 0

            val nevoboCompetitionType = when (ageGroupChar) {
                'Z' -> NevoboCompetitionType.SittingVolleyball
                'R' -> NevoboCompetitionType.IndoorRecreationalVolleyball
                'M' -> NevoboCompetitionType.IndoorMasterCompetitionVolleyball
                else -> NevoboCompetitionType.IndoorVolleyball
            }

            val gender = when (genderChar) {
                'H', 'J' -> Gender.Men
                'D', 'M' -> Gender.Women
                'X', 'N', 'L' -> Gender.Mixed
                else -> throw IllegalArgumentException("Invalid gender character $genderChar")
            }

            val ageGroupIdentifier = when (ageGroupChar) {
                // Z: Sitting Volleyball (assuming always Senior)
                // M: Master volleyball (assuming always Senior)
                // R: Recreational volleyball (assuming always Senior)
                // J: Under 21 = Senior
                'S', 'R', 'Z', 'J', 'M' -> "Senior"
                'A' -> "A"
                'B' -> "B"
                'C' -> "C"
                in '1'..'6' -> "CMV"
                else -> throw IllegalArgumentException("Invalid age group character: $ageGroupChar")
            }

            val nevoboTeamName = NevoboTeamName(
                club,
                nevoboCompetitionType,
                gender,
                NevoboConfig.getAgeGroup(ageGroupIdentifier),
                if (ageGroupIdentifier == "CMV") ageGroupChar.digitToInt() else null,
                teamNumber,
                "${genderChar}${ageGroupChar} $teamNumber",
                teamName
            )
            cache[teamName] = nevoboTeamName
            return nevoboTeamName
        }
    }
}